import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ExtendedProfile } from 'src/app/common/ExtendedProfile';
import { CatalogFilter } from 'src/app/common/CatalogFilter';
import { AppProfileService } from 'src/app/services/profile.service';
import { CatalogItem } from 'src/app/common/CatalogItem';
import { ActivatedRoute, NavigationEnd, ParamMap, Router } from '@angular/router';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faMapMarkerAlt } from '@fortawesome/free-solid-svg-icons';
import { EMPTY, Observable, Subject, catchError, filter, finalize, map, skip, switchMap, take } from 'rxjs';
import { CatalogGeoQuery } from '../../common/Geo';
import { PagedResult } from '../../common/PagedResult';
import { BookmarkService } from '../../services/bookmark.service';
import { CallApiService } from '../../services/api-action.service';
import { CatalogService } from '../../services/catalog.service';
import { PersonalizationService } from '../../services/personalization.service';
import { RADIUS_OPTIONS_KM, SearchLocation, SearchLocationService } from '../../services/search-location.service';
import { UserSettingsService } from '../../services/user-settings.service';
import { DistancePipe } from '../../pipes/distance.pipe';
import { CatalogItemCardComponent } from "../catalog-item-card/catalog-item-card.component";
import { CatalogMapComponent, CatalogMapQuery, CatalogMapSeller } from '../catalog-map/catalog-map.component';
import { LandingPageComponent } from "../landing-page/landing-page.component";
import { LocationPickerComponent } from '../location-picker/location-picker.component';


@Component({
    selector: 'app-search',
    templateUrl: './search.component.html',
    styleUrls: ['./search.component.css'],
    imports: [CatalogItemCardComponent, CatalogMapComponent, LandingPageComponent, LocationPickerComponent, DistancePipe, FontAwesomeModule]
})
export class SearchComponent implements OnInit {
  private route = inject(ActivatedRoute);
  catalogService = inject(CatalogService);
  private profileService = inject(AppProfileService);
  private bookmarkService = inject(BookmarkService);
  private apiActionService = inject(CallApiService);
  private personalizationService = inject(PersonalizationService);
  private locationService = inject(SearchLocationService);
  private router = inject(Router);

  private destroyRef = inject(DestroyRef);

  /** `true` appends the next page („Mehr laden"), `false` replaces the list. */
  private readonly load$ = new Subject<boolean>();

  constructor() {
    // Drive the search off NavigationEnd rather than queryParamMap: a "Suchen" click
    // with unchanged text navigates to the identical /search?... URL, and queryParamMap
    // does NOT re-emit for an unchanged URL — so the search would never re-run. With
    // main.ts's onSameUrlNavigation:'reload', the router still emits a NavigationEnd for
    // that same-URL navigation, so re-reading the query params here re-runs the search.
    // Subscribing in the constructor (not ngOnInit) guarantees we catch the navigation
    // that activates this component — its NavigationEnd fires after construction but
    // before the first change detection (ngOnInit), so an ngOnInit subscription can miss it.
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => this.runSearchFromUrl());

    // #172: a changed position or radius re-runs the active search — the URL did not change,
    // so no NavigationEnd will. skip(1): state$ replays the current state on subscription, and
    // that one is what the navigation's own search already uses.
    this.locationService.state$
      .pipe(skip(1), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (this.filter.isSearchActive) {
          this.reload();
        }
      });

    // One pipeline for every page load, so a request that was superseded — a radius click while
    // a page is still loading, a new search during „Mehr laden" — is cancelled instead of landing
    // on top of its successor (`switchMap`). A failed load keeps the list as it was and toasts;
    // the coded geo refusals (`geoRadiusOutOfRange`, …) get their dictionary sentence that way.
    this.load$
      .pipe(
        switchMap((append) =>
          this.apiActionService
            .callWithToasts$(
              this.catalogService.getBySearch(this.filter, { skip: this.skip, take: this.pageSize }, this.geoQuery()),
              null,
              $localize`:@@search.error:Die Suche konnte nicht ausgeführt werden.`
            )
            .pipe(
              map((result): { append: boolean; result: PagedResult<CatalogItem> } => ({ append, result })),
              catchError(() => EMPTY),
              finalize(() => (this.loadingMore = false))
            )
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(({ append, result }) => {
        this.catalogItems = append ? [...this.catalogItems, ...result.items] : result.items;
        this.totalCount = result.totalCount;
      });
  }

  public profile: ExtendedProfile = null;
  public filter: CatalogFilter = new CatalogFilter('', null);
  // #648: the catalog is paged — accumulate pages for the "Mehr laden" button.
  public catalogItems: CatalogItem[];
  public totalCount = 0;
  public loadingMore = false;
  private skip = 0;
  private pageSize = 50;
  private userSettingsService = inject(UserSettingsService);

  public readonly faMapMarkerAlt = faMapMarkerAlt;
  public readonly radiusOptions = RADIUS_OPTIONS_KM;

  /**
   * #172: what the map draws pins for — a NEW object per search run, so the map notices a
   * re-run (same term, new radius) that leaves the filter's own fields untouched. Null while no
   * search is active, which also unmounts the map.
   */
  public mapQuery: CatalogMapQuery | null = null;

  /**
   * #172: „Nur diesen Anbieter zeigen" from a pin's popup. Narrows the LOADED result on the
   * client — `GET api/catalog/catalogitems` has no seller parameter — so the count line says how
   * many of the loaded entries are theirs and „Mehr laden" keeps working. Cleared by every new
   * search run.
   */
  public selectedSeller: CatalogMapSeller | null = null;

  // #652: the bookmarked listing ids, loaded once (never per card) and kept in sync with
  // the optimistic toggles below. Empty for an anonymous visitor and after a failed load —
  // the cards then render without a marked state and the results list is unaffected.
  public bookmarkedIds: ReadonlySet<string> = new Set<string>();

  ngOnInit() {
    this.profileService.currentProfile$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((p) => {
      this.profile = p;
      // #172 fallback step 2: the own registered address, once per page load and only while
      // nothing was chosen or removed in this tab (the service guards both) — and only for a
      // user the endpoint admits. Its arrival changes state$, which re-runs the search.
      if (p?.hasShopAccess) {
        this.locationService.useCustomerAddress(customerAddressLabel(p)).subscribe();
      }
    });

    this.bookmarkService.ensureIdsLoaded();
    this.bookmarkService.bookmarkedIds$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((ids) => (this.bookmarkedIds = ids));
  }

  /**
   * #652 (decision 3a): the Merkliste API is `[Authorize]`, so an anonymous visitor — who
   * may search and open results — gets no bookmark button rather than one that 401s.
   */
  get canBookmark(): boolean {
    return !!this.profile?.id;
  }

  /** #172: the chosen position, or null — then the list is the plain, unfiltered search. */
  get location(): SearchLocation | null {
    return this.locationService.location;
  }

  /** #172: the radius chip, or null for „egal". */
  get radiusKm(): number | null {
    return this.locationService.radiusKm;
  }

  /** True while the result is the located one: a position AND a radius narrow it. */
  get isLocated(): boolean {
    return this.geoQuery() !== null;
  }

  setRadius(radiusKm: number | null): void {
    this.locationService.setRadius(radiusKm);
  }

  /** The loaded entries, narrowed to the selected seller when there is one. */
  get visibleItems(): CatalogItem[] {
    const items = this.catalogItems ?? [];
    const seller = this.selectedSeller;
    return seller ? items.filter((item) => item.catalogSku?.sellerCustomerId === seller.id) : items;
  }

  selectSeller(seller: CatalogMapSeller): void {
    this.selectedSeller = seller;
  }

  clearSeller(): void {
    this.selectedSeller = null;
  }

  /**
   * The card's `[asyncClick]` factory. The service flips its id set before the request
   * goes out and puts it back if the request fails; the toast here is what makes that
   * rollback visible instead of silent.
   */
  bookmarkAction(catalogSkuId: string): () => Observable<unknown> {
    return () =>
      this.apiActionService.callWithToasts$(
        this.bookmarkService.toggle(catalogSkuId),
        null,
        $localize`:@@catalog.bookmark.error:Die Merkliste konnte nicht geändert werden.`
      );
  }

  private runSearchFromUrl() {
    this.setFilterByParams(this.route.snapshot.queryParamMap);
    if (this.filter.hasValues()) {
      this.search(this.filter);
    } else {
      // #94: an emptied search field navigates here with no query params at all. The filter
      // object survives that navigation (setFilterByParams mutates it in place), so its
      // isSearchActive has to be cleared explicitly — otherwise the results heading stays and
      // the landing page never comes back.
      this.filter.isSearchActive = false;
      this.catalogItems = null;
      this.totalCount = 0;
      this.mapQuery = null;
      this.selectedSeller = null;
    }
  }

  search(filter: CatalogFilter) {
    this.filter = filter;
    this.filter.isSearchActive = true;
    this.recordSearch(filter);
    this.reload();
  }

  /**
   * The first page of the current filter, position and radius. Separate from {@link search}
   * because a radius or location change is not a new search the personalization history should
   * record (#94) — the term is the same.
   */
  private reload() {
    // #648: page size from the user's ListPageSize setting; the cached
    // listPageSize$ replays, so this settles immediately after the first read.
    this.userSettingsService.listPageSize$.pipe(take(1)).subscribe((size) => {
      this.pageSize = size;
      this.skip = 0;
      this.selectedSeller = null;
      this.mapQuery = this.buildMapQuery();
      this.load$.next(false);
    });
  }

  private buildMapQuery(): CatalogMapQuery {
    const location = this.locationService.location;
    const geo = this.geoQuery();
    return {
      name: this.filter.name ?? null,
      categoryId: this.filter.categoryId ?? null,
      productSkuId: this.filter.productSkuId ?? null,
      origin: geo?.origin ?? location ?? null,
      radiusKm: geo?.radiusKm ?? null,
      originLabel: location?.label ?? null,
    };
  }

  public loadMore() {
    if (this.loadingMore) return;
    this.loadingMore = true;
    this.skip += this.pageSize;
    this.load$.next(true);
  }

  /**
   * #172: the geo half of the query — a radius around the chosen position, or nothing.
   *
   * „egal" (radius null) is deliberately the PLAIN search, not the backend's 200 km maximum:
   * the user asked for no distance limit, and a 200 km cap would silently drop every seller
   * beyond it and every seller without a coordinate. The price is that the tiles then carry no
   * distance, because the contract has no „origin only" form (`geoAreaIncomplete`).
   */
  private geoQuery(): CatalogGeoQuery | null {
    const location = this.locationService.location;
    const radiusKm = this.locationService.radiusKm;
    return location && radiusKm != null ? { origin: location, radiusKm } : null;
  }

  /**
   * #94: records the search the user actually RAN — this is the only place in the app that
   * may, because it is the only one that knows a search was executed. The search bar debounces
   * nothing, but it emits on every category change and mirrors the URL, so recording there
   * would fill the history with terms nobody searched for.
   *
   * A term-less search (category only) is not recorded: the backend's term is required and a
   * term that normalizes to nothing is discarded anyway, so the request would be pure noise.
   * Fire-and-forget and silent, like the visit recorder — the results are what the user asked
   * for, and a failed signal must not colour them.
   */
  private recordSearch(filter: CatalogFilter) {
    const term = filter?.name?.trim();
    if (!term) {
      return;
    }
    // Not `this.profile`: the first search of a page load runs from the constructor's
    // NavigationEnd, before ngOnInit has filled that field. The recorder is [Authorize], so
    // an anonymous visitor must be skipped rather than sent into a 401.
    this.profileService.currentProfile$.pipe(take(1)).subscribe((profile) => {
      if (profile?.id) {
        this.personalizationService
          .recordSearch(term, filter.categoryId || null)
          .subscribe({ error: () => {} });
      }
    });
  }

  private setFilterByParams(paramtMap: ParamMap) {
    this.filter.name = paramtMap.get('name');
    this.filter.categoryId = paramtMap.get('categoryId');
    this.filter.productSkuId = paramtMap.get('productSkuId');
  }
}

/** What the header chip shows for the own address: „50667 Köln", or a generic label without one. */
function customerAddressLabel(profile: ExtendedProfile): string {
  const label = [profile.customer?.zipCode, profile.customer?.city]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(' ');
  return label || $localize`:@@searchLocation.customerLabel:Meine Adresse`;
}
